import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import { t } from '../i18n.js';
import { initials } from '../format.js';
import type { Entity } from '../api.js';
import {
  albums,
  edgesOfGroup,
  form,
  freshCount,
  freshOfGroup,
  groups,
  me,
  members,
  view,
} from '../state.js';
import { onDeleteGroup, onLeaveGroup, onRenameGroup, onOpen, openView } from '../actions.js';
import { Action, Empty, IconButton, Pill } from './parts.js';
import { Thumb, filmstripOf } from './gallery.js';
import { MarkdownExcerpt } from '../markdown.js';
import {
  IconCheck,
  IconCollection,
  IconEdit,
  IconLeave,
  IconMembers,
  IconPlus,
  IconTrash,
} from './icons.js';

import { GroupView, roleLabel } from './group.js';

/**
 * Le renvoi vers le « + » quand il n'y a encore aucun groupe. Le geste reste celui
 * du bouton d'ajout — l'état vide ne crée pas un second chemin, il montre le
 * premier.
 */
function NewGroupHint(): JSX.Element {
  return (
    <Action testId="hint-group" variant="quiet" onClick={() => (form.value = 'group')}>
      <IconPlus size={16} />
      <span>{t('group.new')}</span>
    </Action>
  );
}

/**
 * **Mes partages** — *qu'est-ce qui circule, et avec qui ?*
 *
 * ### Il n'y a plus d'onglet « Reçu »
 *
 * L'écran opposait *Reçu* et *Groupes*, ce qui mélangeait une **direction** et un
 * **objet** — et laissait croire à deux inventaires alors qu'il n'y en a qu'un.
 * Ce que j'ai reçu est *dans* les groupes dont je suis membre ; ce que j'ai donné
 * est le même contenu vu de l'autre bout. Une liste, donc : les groupes.
 *
 * L'autre direction — *à qui ai-je donné ceci ?* — n'est pas une liste mais une
 * **propriété d'objet** : elle se lit dans l'inspecteur (« où va ma donnée »,
 * `GET /files/{fid}/shares`), avec le retrait souverain juste à côté. Cette
 * question se pose toujours à propos d'une chose, jamais dans l'abstrait, et une
 * liste globale aurait redérivé la même donnée sans offrir où agir.
 *
 * ### La pastille de nouveautés n'est pas un « non lu »
 *
 * Elle compte ce qui est arrivé dans les sept derniers jours, et rien d'autre. Un
 * non-lu exigerait de mémoriser ce qui a été vu, par groupe et par appareil, et de
 * décider ce que « vu » veut dire — avoir ouvert l'onglet ? avoir défilé jusqu'en
 * bas ? Une fenêtre ne prétend rien de tout cela ; elle dit *il s'est passé
 * quelque chose récemment*, ce qu'une pastille peut porter honnêtement.
 */
export function SharedView(): JSX.Element {
  // Un groupe ouvert **remplace** la liste plutôt que de s'afficher à côté : le
  // rail dit déjà où l'on est, et deux niveaux visibles simultanément feraient
  // hésiter sur ce que le « + » et la barre d'actions visent.
  if (view.value.kind === 'group') return <GroupView />;

  const loaded = groups.value;
  // Tant que `GET /groups` n'a pas répondu, on montre **ce que `GET /me` porte
  // déjà** : l'appartenance est connue depuis la connexion, et faire patienter
  // devant une information qu'on a est un mensonge par omission.
  const rows =
    loaded ??
    (me.value?.groups ?? []).map((g) => ({
      gid: g.gid,
      name: g.name,
      role: g.role,
      memberCount: 0,
      createdAt: '',
    }));

  if (rows.length === 0) {
    return (
      <div data-testid="groups">
        <Empty action={<NewGroupHint />}>{t('state.emptyGroups')}</Empty>
      </div>
    );
  }

  return (
    <>
      <ul class="cards" data-testid="groups">
        {rows.map((g) => {
          const fresh = freshCount(g.gid);
          const shared = edgesOfGroup(g.gid).length;
          const collections = (albums.value ?? []).filter((a) =>
            (a.authGroups ?? []).includes(g.gid),
          ).length;
          const roster = members.value[g.gid];

          return (
            <li key={g.gid} class="card group-card" data-testid={`group-${g.gid}`}>
              <button
                type="button"
                class="card-open"
                data-testid={`open-group-${g.gid}`}
                onClick={() => openView({ kind: 'group', gid: g.gid, tab: 'collections' })}
              >
                <span class="avatar" aria-hidden="true">
                  {initials(g.name)}
                </span>
                <span class="grow">
                  <span class="card-title">{g.name}</span>
                  {/* **Chaque fait dans son `<span>`, et la ligne peut passer à
                      la suivante.** Les trois chiffres étaient trois nœuds de
                      texte nus dans un conteneur `flex` : chacun devenait donc un
                      item anonyme, et sous 52 rem ils se partageaient la largeur
                      en trois colonnes trop étroites — « 3 mem… », « 2 col… »,
                      « 12 obj… » les uns sur les autres. Un texte n'est pas une
                      colonne : il se coupe où il peut, à condition qu'on ne le
                      découpe pas d'abord. */}
                  <span class="card-meta">
                    <span class="fact">
                      <IconMembers size={14} />
                      {t('shared.members', { count: roster?.length ?? g.memberCount })}
                    </span>
                    <span class="fact">{t('group.collectionCount', { count: collections })}</span>
                    <span class="fact">{t('group.itemCount', { count: shared })}</span>
                  </span>
                </span>
                <span class="card-badges">
                  {/* Ce qui vient d'arriver, **sur la ligne du groupe** : c'est le
                      seul endroit d'où l'on peut apprendre qu'il faut aller voir. */}
                  {fresh > 0 ? (
                    <Pill tone="accent" testId={`fresh-${g.gid}`}>
                      {t('group.fresh', { count: fresh })}
                    </Pill>
                  ) : null}
                  <Pill tone={g.role === 'owner' ? 'accent' : 'neutral'}>{roleLabel(g.role)}</Pill>
                </span>
              </button>

              {/* **Ce qui vient d'arriver, en images** *(2026-08-09)*. La
                  pastille disait *combien* ; il fallait ouvrir le groupe puis
                  l'onglet *Nouveautés* pour apprendre qu'il s'agissait de deux
                  photos déjà vues. La bande le montre sur la ligne du groupe,
                  qui est l'endroit d'où l'on décide d'aller voir ou non.

                  Elle est **hors du bouton d'ouverture** : chaque vignette ouvre
                  *sa* chose, et un bouton dans un bouton n'est pas un document
                  valide — le clic serait pris par le mauvais des deux. */}
              <FreshStrip gid={g.gid} />
            </li>
          );
        })}
      </ul>
    </>
  );
}

/**
 * **La bande de nouveautés d'un groupe.**
 *
 * Elle ne coûte **aucune requête** : les arêtes sont drainées par `/changes` et
 * portent leur projection (D28), et les vignettes passent par le lot de
 * présignatures déjà utilisé par la grille — six vignettes, une entrée de lot.
 *
 * Elle est **bornée à six**. Une bande qui défile sur trente éléments redit ce
 * que l'onglet *Nouveautés* fait mieux, et pousse les autres groupes hors de
 * l'écran : ce qu'on veut ici est *savoir s'il faut y aller*, pas parcourir.
 * Au-delà, la dernière case renvoie au fil complet.
 */
const FRESH_SHOWN = 6;

function FreshStrip({ gid }: { readonly gid: string }): JSX.Element | null {
  const fresh = freshOfGroup(gid);
  if (fresh.length === 0) return null;

  const shown = fresh.slice(0, FRESH_SHOWN);
  const rest = fresh.length - shown.length;

  return (
    <div class="fresh-strip" data-testid={`fresh-strip-${gid}`}>
      {shown.map((item) => (
        <FreshTile key={item.Fid ?? item.Aid} item={item} gid={gid} />
      ))}
      {/* **Toujours présent, même sans reste à voir.** C'est la seule porte vers
          le fil complet depuis cet écran, et un raccourci qui n'apparaît que
          quand il y a plus de six choses ne s'apprend jamais. */}
      <button
        type="button"
        class="fresh-more"
        data-testid={`fresh-all-${gid}`}
        onClick={() => openView({ kind: 'group', gid, tab: 'feed' })}
      >
        {rest > 0 ? `+${rest}` : null}
        <span class="fresh-more-label">{t('group.seeAll')}</span>
      </button>
    </div>
  );
}

/**
 * Une vignette de la bande — **la même grammaire que la tuile et que le fil** :
 * l'image quand il y en a une, l'extrait quand c'est une note, l'extension
 * sinon. Un mur d'icônes identiques dirait qu'il s'est passé quelque chose sans
 * permettre d'en reconnaître une seule.
 */
function FreshTile({ item, gid }: { readonly item: Entity; readonly gid: string }): JSX.Element {
  const id = item.Fid ?? item.Aid ?? '';
  const thumbable = item.Variants?.['thumb']?.size !== undefined;

  return (
    <button
      type="button"
      class="fresh-tile"
      data-testid={`fresh-item-${id}`}
      title={item.Name}
      aria-label={item.Name}
      // Le `gid` est un **indice de résolution**, jamais un droit : il épargne au
      // serveur de sonder les groupes de l'appelant pour un `fid` qu'il ne
      // possède pas. La décision reste portée par l'arête.
      onClick={() => onOpen(item, fresh(gid))}
    >
      {thumbable ? (
        <Thumb fid={id} alt={item.Name} gid={gid} strip={filmstripOf(item)} />
      ) : item.Aid !== undefined ? (
        /* **Une collection se dessine, elle ne s'épelle pas** *(2026-08-14)*.
           Le repli affichait `Type` en capitales : une nouvelle collection
           apparaissait donc comme une case portant le mot « ALBUM » — le nom
           interne du type, dans une bande dont tout le reste est une image, et
           sans dire *laquelle*. Elle porte désormais le pictogramme des
           collections et son nom, comme partout ailleurs dans l'interface. */
        <span class="fresh-album" data-testid={`fresh-album-${id}`}>
          <IconCollection size={20} />
          <span class="fresh-album-name">{item.Name}</span>
        </span>
      ) : item.Excerpt ? (
        /* Le papier, comme dans la grille et dans le fil : une note se reconnaît
           à sa matière autant qu'à son texte. */
        <span class="fresh-excerpt paper">
          <MarkdownExcerpt source={String(item.Excerpt)} testId={`fresh-excerpt-${id}`} />
        </span>
      ) : (
        /* Pour un document, l'**extension** et non le type : « PDF » dit quelque
           chose, « DOC » est le nom du type interne et n'en dit aucun. */
        <span class="fresh-kind">
          {(item.Variants?.['source']?.ext ?? item.Type ?? '').toUpperCase()}
        </span>
      )}
    </button>
  );
}

/** La liste que la visionneuse parcourra : celle de la bande, pas le miroir. */
const fresh = (gid: string): readonly Entity[] => freshOfGroup(gid);

/**
 * La barre d'actions d'un groupe — **renommer, supprimer, quitter**.
 *
 * Elle vit dans l'en-tête de la vue, à côté du titre, comme celle de *Mon espace* :
 * ce sont des actions sur **le lieu où l'on est**, pas sur ce qu'il contient.
 *
 * Deux d'entre elles sont irréversibles et demandent donc une confirmation *sur
 * place* plutôt qu'une boîte de dialogue : supprimer un groupe tombstone toutes
 * ses appartenances en une transaction — chaque membre en sort à l'instant même —
 * et rien ne le restaure, un cercle n'ayant pas de corbeille.
 */
export function GroupToolbar(): JSX.Element | null {
  const v = view.value;
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState('');
  const [confirm, setConfirm] = useState<'delete' | 'leave' | undefined>(undefined);

  if (v.kind !== 'group') return null;

  const group = (groups.value ?? []).find((g) => g.gid === v.gid);
  const roster = members.value[v.gid];
  const owner =
    roster?.some((m) => m.uid === me.value?.uid && m.role === 'owner') ?? group?.role === 'owner';

  if (renaming) {
    return (
      <div class="inline-form">
        <input
          type="text"
          class="input"
          data-testid="rename-group-name"
          aria-label={t('group.rename')}
          value={name}
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
          onKeyDown={(e) => {
            if ((e as KeyboardEvent).key !== 'Enter') return;
            const trimmed = name.trim();
            if (!trimmed) return;
            setRenaming(false);
            void onRenameGroup(v.gid, trimmed);
          }}
        />
        <Action
          testId="rename-group"
          variant="primary"
          onClick={() => {
            const trimmed = name.trim();
            if (!trimmed) return;
            setRenaming(false);
            void onRenameGroup(v.gid, trimmed);
          }}
        >
          <IconCheck size={18} />
          <span>{t('inspector.rename')}</span>
        </Action>
      </div>
    );
  }

  if (confirm) {
    return (
      <div class="confirm">
        <span class="confirm-text">
          {confirm === 'delete' ? t('group.confirmDelete') : t('group.confirmLeave')}
        </span>
        <Action
          testId={confirm === 'delete' ? 'confirm-delete-group' : 'confirm-leave-group'}
          variant="danger"
          onClick={() => {
            setConfirm(undefined);
            void (confirm === 'delete' ? onDeleteGroup(v.gid) : onLeaveGroup(v.gid));
          }}
        >
          {confirm === 'delete' ? t('group.delete') : t('group.leave')}
        </Action>
        <Action testId="cancel-group-action" variant="quiet" onClick={() => setConfirm(undefined)}>
          {t('app.cancel')}
        </Action>
      </div>
    );
  }

  return (
    <>
      {owner ? (
        <>
          <IconButton
            testId="edit-group"
            label={t('group.rename')}
            onClick={() => {
              setName(group?.name ?? '');
              setRenaming(true);
            }}
          >
            <IconEdit size={18} />
          </IconButton>
          <IconButton
            testId="delete-group"
            label={t('group.delete')}
            onClick={() => setConfirm('delete')}
          >
            <IconTrash size={18} />
          </IconButton>
        </>
      ) : (
        // **D16 — quitter est en self-service.** Sans cette porte, sortir d'un
        // groupe demandait la bonne volonté de son propriétaire.
        <IconButton testId="leave-group" label={t('group.leave')} onClick={() => setConfirm('leave')}>
          <IconLeave size={18} />
        </IconButton>
      )}
    </>
  );
}
